/* Prayer makruh/forbidden windows — per-madhab data + geometry/compute.
 *
 * SOURCE OF TRUTH: ported VERBATIM from the landing prayer widget
 * (`prayer-hero.js` → PH_WINDOWS_BY_MADHAB, PH_WINDOWS_I18N) which itself
 * came from landing `script.js` lines 2605-3015. The ruling text below is
 * copied byte-for-byte and MUST NOT be edited or invented — see the project
 * manifest and the port guide gotcha #7. The compute helpers reproduce the
 * derived-window math from the widget's AP_PRAYER_HERO.applyTimings().
 *
 * This module is pure (no network, no DOM) so it satisfies the manifest's
 * "external APIs only via functions/api/*" invariant: it is fed the app's
 * already-proxied timings.
 */

export type Madhab = 'hanafi' | 'shafii' | 'hanbali' | 'maliki';
export const MADHABS: Madhab[] = ['hanafi', 'shafii', 'hanbali', 'maliki'];

export type Locale = 'en' | 'ur' | 'ar';

/** strict: true = haram/forbidden, false = makruh, null = permissible (ishraq), "note" = informational */
export type WindowStrict = true | false | null | 'note';

export interface PrayerWindow {
  key: string;
  label: string;
  ar: string;
  /** HH:MM clock anchors used in the static (Faisalabad) data; overridden at runtime by derived times keyed on `key`. */
  from?: string | null;
  to?: string | null;
  fromMin?: number;
  toMin?: number;
  strict: WindowStrict;
  note: string;
  exception: string | null;
}

export const PH_MADHAB_LABELS: Record<Madhab, { name: string; ar: string; ur: string }> = {
  hanafi: { name: 'Hanafi', ar: 'حنفي', ur: 'حنفی' },
  shafii: { name: "Shafi'i", ar: 'شافعي', ur: 'شافعی' },
  hanbali: { name: 'Hanbali', ar: 'حنبلي', ur: 'حنبلی' },
  maliki: { name: 'Maliki', ar: 'مالكي', ur: 'مالکی' }
};

/* ── Per-madhab window definitions (English, VERBATIM) ─────────────────────── */

export const PH_WINDOWS_BY_MADHAB: Record<Madhab, PrayerWindow[]> = {
  hanafi: [
    {
      key: 'afterFajr', label: 'After Fajr fard', ar: 'بعد فرض الفجر',
      strict: false,
      note: 'Makruh tahrimi (prohibitively disliked) — no voluntary (nafl) prayers after completing the Fajr fard until sunrise. The two emphasized Fajr sunnah must be prayed before the fard, not after.',
      exception: 'Qada (missed obligatory) prayers are permitted during this window.'
    },
    {
      key: 'sunrise', label: 'Sunrise', ar: 'وقت الشروق',
      strict: true,
      note: "Haram — all prayer is forbidden from the moment the sun begins to appear on the horizon until it rises to a spear's height (~15–20 minutes). Any prayer offered in this window is invalid in the Hanafi school.",
      exception: 'Sajda Tilawah (Quranic prostration) is technically permissible but slightly disliked (makruh tanzihan).'
    },
    {
      key: 'ishraq', label: 'Ishraq (permissible)', ar: 'صلاة الإشراق',
      strict: null,
      note: "The first permissible voluntary prayer window of the day, beginning ~20 minutes after sunrise. Praying two rak'ahs here after remaining seated in dhikr from Fajr until this time carries the reward of a complete Hajj and Umrah (Tirmidhi).",
      exception: null
    },
    {
      key: 'zawal', label: 'Zawal (zenith)', ar: 'وقت الزوال',
      strict: true,
      note: 'Haram — all prayer is forbidden while the sun is at its absolute peak in the sky, for a brief window of approximately 5–15 minutes immediately before Dhuhr begins. Any prayer offered at this moment is invalid.',
      exception: 'Sajda Tilawah (Quranic prostration) is technically permissible but slightly disliked (makruh tanzihan).'
    },
    {
      key: 'afterAsr', label: 'After Asr fard', ar: 'بعد فرض العصر',
      strict: false,
      note: 'Makruh tahrimi (prohibitively disliked) — no voluntary (nafl) prayers after completing the Asr fard until the sun begins to visibly pale before sunset. The four emphasized Asr sunnah must be prayed before the fard, not after.',
      exception: 'Qada (missed obligatory) prayers are permitted during this window.'
    },
    {
      key: 'sunset', label: 'Sunset', ar: 'وقت الغروب',
      strict: true,
      note: "Haram — all prayer is forbidden once the sun's glare diminishes enough to look at it directly (~20 minutes before Maghrib) until it fully disappears below the horizon. Any prayer offered in this window is invalid.",
      exception: 'Exception: if Asr fard has not yet been prayed that day, it may still be offered during this window and remains valid.'
    }
  ],

  shafii: [
    {
      key: 'afterFajr', label: 'After Fajr prayer', ar: 'بعد صلاة الفجر',
      strict: false,
      note: "Makruh tahrimi for nafl prayers without a preceding cause. In the Shafi'i school all prayers during forbidden times remain valid — none are rendered invalid. The restriction begins after performing the Fajr obligatory prayer, not at the adhan.",
      exception: 'Dhul-sabab exception: prayers with a preceding cause — greeting the mosque (tahiyyatul masjid), post-wudu prayer, eclipse prayer — are permitted even during this window. Qada (makeup) prayers are also permitted.'
    },
    {
      key: 'sunrise', label: 'Sunrise', ar: 'وقت الشروق',
      strict: false,
      note: "Makruh tahrimi for nafl without a preceding cause, from the moment the sun begins to rise until it reaches a spear's height (~15–20 minutes). Unlike the Hanafi school, prayers offered during this time are valid though sinful — they are not invalidated.",
      exception: 'Dhul-sabab exception: cause-based nafl (tahiyyatul masjid, post-wudu, etc.) are permitted. Qada prayers are permitted. Prayers in Makkah are never restricted.'
    },
    {
      key: 'ishraq', label: 'Ishraq (permissible)', ar: 'صلاة الإشراق',
      strict: null,
      note: "The first permissible voluntary prayer window of the day, beginning ~20 minutes after sunrise. Praying two rak'ahs here after remaining seated in dhikr from Fajr until this time carries the reward of a complete Hajj and Umrah (Tirmidhi).",
      exception: null
    },
    {
      key: 'zawal', label: 'Zawal (zenith)', ar: 'وقت الزوال',
      strict: false,
      note: "Makruh tahrimi for nafl without a cause, while the sun is at its exact peak immediately before Dhuhr (~5–15 minutes). Prayers remain valid in the Shafi'i school — they are disliked but not invalidated.",
      exception: "Friday exception: zawal is permissible on Jumu'ah. Dhul-sabab prayers (cause-based nafl) are permitted. Prayers in Makkah are unrestricted at all times."
    },
    {
      key: 'afterAsr', label: 'After Asr prayer', ar: 'بعد صلاة العصر',
      strict: false,
      note: 'Makruh tahrimi for nafl without a preceding cause, from after completing the Asr obligatory prayer until the sun begins to turn noticeably yellow. All prayers during this window remain valid.',
      exception: 'Dhul-sabab exception: cause-based prayers are permitted. Qada prayers are permitted. Prayers in Makkah are unrestricted.'
    },
    {
      key: 'sunset', label: 'Sunset', ar: 'وقت الغروب',
      strict: false,
      note: "Makruh tahrimi for nafl without a cause, from when the sun's light turns noticeably yellow until it fully sets (~20 minutes). Prayers remain valid — not invalidated in the Shafi'i school.",
      exception: 'Dhul-sabab exception: cause-based nafl (tahiyyatul masjid, post-wudu, etc.) are permitted. Qada prayers are permitted. Prayers in Makkah are unrestricted.'
    }
  ],

  hanbali: [
    {
      key: 'afterFajrAdhan', label: 'After Fajr adhan', ar: 'بعد أذان الفجر',
      strict: true,
      note: "In the Hanbali school, the restriction begins at the Fajr adhan — earlier than other schools. No nafl prayers from the Fajr call to prayer until the sun rises to a spear's height. This is a stricter starting point than Hanafi (which starts after completing the fard).",
      exception: 'The two emphasized Fajr sunnah may still be prayed before the fard. Qada (makeup) prayers, oath-obligated prayers, and tawaf prayers are permitted during this window.'
    },
    {
      key: 'sunrise', label: 'Sunrise', ar: 'وقت الشروق',
      strict: true,
      note: "Forbidden for nafl from when the sun appears on the horizon until it reaches a spear's height (~15–20 minutes). This is a continuation of the post-Fajr adhan window and carries the same ruling.",
      exception: 'Qada prayers, oath-obligated prayers, funeral prayers (if there is fear for the deceased), and tawaf prayers are all permitted during this window.'
    },
    {
      key: 'ishraq', label: 'Ishraq (permissible)', ar: 'صلاة الإشراق',
      strict: null,
      note: "The first permissible voluntary prayer window of the day, beginning ~20 minutes after sunrise. Praying two rak'ahs here after remaining seated in dhikr from Fajr until this time carries the reward of a complete Hajj and Umrah (Tirmidhi).",
      exception: null
    },
    {
      key: 'zawal', label: 'Zawal (zenith)', ar: 'وقت الزوال',
      strict: true,
      note: 'Forbidden for nafl while the sun is at its exact zenith, for a brief window of approximately 5–15 minutes before Dhuhr. This applies equally in the Hanbali school.',
      exception: 'Qada prayers, oath-obligated prayers, tawaf prayers, and repeating a prayer in congregation are permitted.'
    },
    {
      key: 'afterAsr', label: 'After Asr fard', ar: 'بعد فرض العصر',
      strict: true,
      note: "Forbidden for nafl from after completing the Asr fard until complete sunset. Note: this window extends all the way to full sunset in the Hanbali school — there is no separate 'sunset begins' sub-window.",
      exception: 'Qada prayers, oath-obligated prayers, funeral prayers (if fear for the deceased), tawaf prayers, and repeating a prayer in congregation are all permitted.'
    }
  ],

  maliki: [
    {
      key: 'sunrise', label: 'Sunrise', ar: 'وقت الشروق',
      strict: false,
      note: 'Makruh (disliked) — voluntary prayers are discouraged while the sun is physically rising, from when it first appears on the horizon until it fully clears it (~15–20 minutes). Prayers during this time are valid but disliked.',
      exception: 'Qada (makeup) prayers are permitted. Obligatory prayers are unaffected.'
    },
    {
      key: 'ishraq', label: 'Ishraq (permissible)', ar: 'صلاة الإشراق',
      strict: null,
      note: "The first permissible voluntary prayer window of the day, beginning ~20 minutes after sunrise. Praying two rak'ahs here after remaining seated in dhikr from Fajr until this time carries the reward of a complete Hajj and Umrah (Tirmidhi).",
      exception: null
    },
    {
      key: 'yellowSun', label: 'Sun turns yellow (before sunset)', ar: 'وقت الاصفرار',
      strict: false,
      note: "Makruh (disliked) — voluntary prayers are discouraged once the sun's light turns noticeably yellow and dims before sunset (~20 minutes before Maghrib). This is one of only two disliked windows in the Maliki school. Prayers remain valid.",
      exception: 'Qada prayers are permitted. Obligatory prayers are unaffected.'
    },
    {
      key: 'malikirule', label: 'Note: Zawal is not restricted', ar: 'ملاحظة: الزوال',
      from: null, to: null, strict: 'note',
      note: 'Unlike the other three schools, the Maliki school does not restrict prayer at zawal (solar zenith). Praying at midday before Dhuhr enters is permissible in the Maliki school — there is no makruh at this time.',
      exception: null
    },
    {
      key: 'malikirule2', label: 'Unique Maliki rule: Qada takes priority', ar: 'حكم خاص: القضاء أولاً',
      from: null, to: null, strict: 'note',
      note: 'A distinctive Maliki ruling: if a person owes any missed obligatory (qada) prayers, they should not pray voluntary (nafl) prayers until those qada are made up. This applies outside of forbidden times as a general principle, not just during these windows.',
      exception: null
    }
  ]
};

/* ── Arabic & Urdu translations (VERBATIM) ─────────────────────────────────── */

export interface WindowI18nEntry {
  label: string;
  note: string;
  exception: string | null;
}
export interface WindowI18nUi {
  toggleLabel: string;
  schoolOfLaw: string;
  badgeHaram: string;
  badgeMakruhT: string;
  badgeMakruh: string;
  badgeOk: string;
  badgeInfo: string;
}
export interface WindowI18nLocale {
  _ui: WindowI18nUi;
  hanafi: Record<string, WindowI18nEntry>;
  shafii: Record<string, WindowI18nEntry>;
  hanbali: Record<string, WindowI18nEntry>;
  maliki: Record<string, WindowI18nEntry>;
}

export const PH_WINDOWS_I18N: Record<'ar' | 'ur', WindowI18nLocale> = {
  ar: {
    _ui: {
      toggleLabel: 'أوقات الكراهة والحظر',
      schoolOfLaw: 'المذهب الفقهي',
      badgeHaram: 'حرام',
      badgeMakruhT: 'مكروه تحريمي',
      badgeMakruh: 'مكروه',
      badgeOk: 'مباح',
      badgeInfo: 'تنبيه'
    },
    hanafi: {
      afterFajr: {
        label: 'بعد فريضة الفجر',
        note: 'مكروه تحريمي — لا يجوز أداء النوافل بعد الانتهاء من فريضة الفجر حتى طلوع الشمس. وسنة الفجر المؤكدة (ركعتا السنة) تُؤدَّى قبل الفرض لا بعده.',
        exception: 'يجوز قضاء الفرائض الفائتة في هذا الوقت.'
      },
      sunrise: {
        label: 'وقت الشروق',
        note: 'حرام — يحرم أداء أي صلاة من لحظة بدء طلوع الشمس حتى ارتفاعها قدر رمح (نحو ١٥–٢٠ دقيقة). وأي صلاة تُؤدَّى في هذا الوقت تكون باطلة عند الحنفية.',
        exception: 'سجدة التلاوة جائزة تقنياً مع كراهة تنزيهية خفيفة.'
      },
      ishraq: {
        label: 'صلاة الإشراق (وقت مباح)',
        note: 'أول وقت مباح لصلاة النوافل، يبدأ بعد نحو ٢٠ دقيقة من الشروق. ومن جلس في ذكر الله من صلاة الفجر حتى هذا الوقت ثم صلى ركعتين، نال ثواب حجة وعمرة تامتين كاملتين. (رواه الترمذي)',
        exception: null
      },
      zawal: {
        label: 'وقت الزوال (الاستواء)',
        note: 'حرام — يحرم أداء أي صلاة والشمس في كبد السماء، في نافذة قصيرة نحو ٥–١٥ دقيقة قُبيل دخول وقت الظهر. وأي صلاة تُؤدَّى في هذه اللحظة تكون باطلة.',
        exception: 'سجدة التلاوة جائزة مع كراهة تنزيهية.'
      },
      afterAsr: {
        label: 'بعد فريضة العصر',
        note: 'مكروه تحريمي — لا يجوز التنفل بعد أداء فريضة العصر حتى تبدأ الشمس في الاصفرار. وسنة العصر الأربع المؤكدة تُؤدَّى قبل الفرض لا بعده.',
        exception: 'يجوز قضاء الفرائض الفائتة في هذا الوقت.'
      },
      sunset: {
        label: 'وقت الغروب',
        note: 'حرام — من حين اصفرار ضوء الشمس وضعف شعاعها (نحو ٢٠ دقيقة قبل المغرب) حتى غروبها الكامل، يحرم الصلاة وتبطل إن أُدِّيت.',
        exception: 'استثناء: إن لم يُؤدَّ فرض العصر في ذلك اليوم بعد، فلا يزال يجوز أداؤه في هذا الوقت ويُعدُّ صحيحاً.'
      }
    },
    shafii: {
      afterFajr: {
        label: 'بعد صلاة الفجر',
        note: 'مكروه تحريمي للنوافل المطلقة (بلا سبب سابق). عند الشافعية تبقى جميع الصلوات المؤداة في أوقات الكراهة صحيحة ومجزئة — لا تبطل. والتقييد يبدأ بعد أداء الفريضة لا من وقت الأذان.',
        exception: 'استثناء ذو السبب: الصلوات ذات الأسباب السابقة — تحية المسجد، صلاة الوضوء، صلاة الكسوف — جائزة في هذا الوقت. وكذلك قضاء الفوائت.'
      },
      sunrise: {
        label: 'وقت الشروق',
        note: 'مكروه تحريمي للنوافل المطلقة، من لحظة بدء طلوع الشمس حتى ارتفاعها قدر رمح (١٥–٢٠ دقيقة). وخلافاً للحنفية، الصلاة في هذا الوقت عند الشافعية صحيحة وإن كانت مذمومة.',
        exception: 'استثناء ذو السبب: النوافل المرتبطة بسبب (تحية المسجد، صلاة الوضوء...) مباحة. والقضاء جائز. والصلاة في مكة المكرمة غير مقيدة في أي وقت.'
      },
      ishraq: {
        label: 'صلاة الإشراق (وقت مباح)',
        note: 'أول وقت مباح لصلاة النوافل، يبدأ بعد نحو ٢٠ دقيقة من الشروق. ومن جلس في ذكر الله من صلاة الفجر حتى هذا الوقت ثم صلى ركعتين، نال ثواب حجة وعمرة تامتين. (رواه الترمذي)',
        exception: null
      },
      zawal: {
        label: 'وقت الزوال (الاستواء)',
        note: 'مكروه تحريمي للنوافل المطلقة أثناء استواء الشمس قُبيل دخول الظهر (٥–١٥ دقيقة). الصلاة صحيحة عند الشافعية وإن كانت مكروهة.',
        exception: 'استثناء الجمعة: يُباح التنفل عند الزوال يوم الجمعة. وذو السبب جائز. والصلاة في مكة المكرمة مباحة على الإطلاق.'
      },
      afterAsr: {
        label: 'بعد صلاة العصر',
        note: 'مكروه تحريمي للنوافل المطلقة من بعد أداء فريضة العصر حتى بدء اصفرار الشمس. جميع الصلوات في هذا الوقت صحيحة عند الشافعية.',
        exception: 'استثناء ذو السبب: الصلوات ذات الأسباب مباحة. والقضاء جائز. والصلاة في مكة مباحة.'
      },
      sunset: {
        label: 'وقت الغروب',
        note: 'مكروه تحريمي للنوافل المطلقة من بداية اصفرار الشمس حتى الغروب الكامل (نحو ٢٠ دقيقة). الصلاة صحيحة ولا تبطل عند الشافعية.',
        exception: 'استثناء ذو السبب: النوافل ذات السبب مباحة. والقضاء جائز. والصلاة في مكة مباحة.'
      }
    },
    hanbali: {
      afterFajrAdhan: {
        label: 'بعد أذان الفجر',
        note: 'يبدأ التقييد في المذهب الحنبلي من أذان الفجر — وهو أسبق من سائر المذاهب. لا يجوز أداء أي نافلة من الأذان حتى ارتفاع الشمس قدر رمح.',
        exception: 'يجوز أداء سنة الفجر المؤكدة قبل الفريضة. والقضاء وصلاة النذر وصلاة الطواف مباحة في هذا الوقت.'
      },
      sunrise: {
        label: 'وقت الشروق',
        note: 'ممنوع للنوافل من بزوغ الشمس حتى ارتفاعها قدر رمح (١٥–٢٠ دقيقة)، وهو امتداد لوقت ما بعد أذان الفجر ويحمل حكمه.',
        exception: 'يجوز القضاء وصلاة النذر وصلاة الجنازة (عند الخوف على الميت) وصلاة الطواف في هذا الوقت.'
      },
      ishraq: {
        label: 'صلاة الإشراق (وقت مباح)',
        note: 'أول وقت مباح لصلاة النوافل، يبدأ بعد نحو ٢٠ دقيقة من الشروق. ومن جلس في ذكر الله من صلاة الفجر حتى هذا الوقت ثم صلى ركعتين، نال ثواب حجة وعمرة تامتين. (رواه الترمذي)',
        exception: null
      },
      zawal: {
        label: 'وقت الزوال (الاستواء)',
        note: 'ممنوع للنوافل عند استواء الشمس، قُبيل دخول الظهر بنحو ٥–١٥ دقيقة. وينطبق هذا الحكم عند الحنابلة كما عند سائر المذاهب.',
        exception: 'يجوز القضاء والمنذور وصلاة الطواف وإعادة الصلاة في جماعة في هذا الوقت.'
      },
      afterAsr: {
        label: 'بعد فريضة العصر',
        note: 'ممنوع للنوافل من بعد أداء فريضة العصر حتى الغروب التام. تنبيه: يمتد هذا الوقت عند الحنابلة حتى الغروب الكامل دون تفريق بين بداية الاصفرار والغروب.',
        exception: 'يجوز القضاء والمنذور وصلاة الجنازة (عند الخوف على الميت) وصلاة الطواف وإعادة الصلاة في جماعة.'
      }
    },
    maliki: {
      sunrise: {
        label: 'وقت الشروق',
        note: 'مكروه — تُكره النوافل أثناء بزوغ الشمس، من بدء ظهورها حتى ارتفاعها الكامل (١٥–٢٠ دقيقة). الصلاة صحيحة لكنها مكروهة.',
        exception: 'قضاء الفرائض مباح. وأداء الفرائض لا يتأثر.'
      },
      ishraq: {
        label: 'صلاة الإشراق (وقت مباح)',
        note: 'أول وقت مباح لصلاة النوافل، يبدأ بعد نحو ٢٠ دقيقة من الشروق. ومن جلس في ذكر الله من صلاة الفجر حتى هذا الوقت ثم صلى ركعتين، نال ثواب حجة وعمرة تامتين. (رواه الترمذي)',
        exception: null
      },
      yellowSun: {
        label: 'وقت الاصفرار (قُبيل المغرب)',
        note: 'مكروه — تُكره النوافل حين يصفار ضوء الشمس ويخفت قُبيل المغرب (نحو ٢٠ دقيقة). وهذا أحد الوقتين المكروهين فقط في المذهب المالكي. الصلاة صحيحة.',
        exception: 'قضاء الفرائض مباح. وأداء الفرائض لا يتأثر.'
      },
      malikirule: {
        label: 'تنبيه: الزوال غير مقيد',
        note: 'خلافاً للمذاهب الثلاثة الأخرى، لا تُكره الصلاة عند الزوال في المذهب المالكي. فالتنفل قُبيل دخول الظهر مباح تماماً، ولا كراهة في ذلك.',
        exception: null
      },
      malikirule2: {
        label: 'حكم مالكي خاص: القضاء أولاً',
        note: 'حكم مميز في المذهب المالكي: من عليه قضاء فرائض فاتته، فلا ينبغي له أن يتطوع بالنوافل حتى يقضيها. وهذا مبدأ عام لا يختص بأوقات الكراهة وحدها.',
        exception: null
      }
    }
  },

  ur: {
    _ui: {
      toggleLabel: 'اوقاتِ کراہت و ممانعت',
      schoolOfLaw: 'فقہی مذہب',
      badgeHaram: 'حرام',
      badgeMakruhT: 'مکروہ تحریمی',
      badgeMakruh: 'مکروہ',
      badgeOk: 'جائز',
      badgeInfo: 'نوٹ'
    },
    hanafi: {
      afterFajr: {
        label: 'فجر فرض کے بعد',
        note: 'مکروہ تحریمی — فجر کا فرض ادا کرنے کے بعد طلوعِ آفتاب تک نفل نماز پڑھنا مکروہ تحریمی ہے۔ فجر کی دونوں مؤکد سنتیں فرض سے پہلے پڑھی جائیں، بعد میں نہیں۔',
        exception: 'اس وقت میں فائتہ فرض نمازوں کی قضا جائز ہے۔'
      },
      sunrise: {
        label: 'وقتِ طلوعِ آفتاب',
        note: 'حرام — جب سے سورج افق پر نمودار ہو، نیزے کی اونچائی (تقریباً ١٥–٢٠ منٹ) تک ہر نماز ممنوع ہے۔ حنفی مذہب میں اس وقت پڑھی گئی نماز باطل ہوتی ہے۔',
        exception: 'سجدۂ تلاوت تکنیکی طور پر جائز ہے البتہ مکروہ تنزیہی ہے۔'
      },
      ishraq: {
        label: 'نمازِ اشراق (جائز وقت)',
        note: 'دن کا پہلا جائز نفل وقت، طلوعِ آفتاب کے تقریباً ٢٠ منٹ بعد شروع ہوتا ہے۔ جو شخص فجر کے بعد ذکرِ الہٰی میں بیٹھا رہے یہاں تک کہ اشراق کی نماز پڑھے، اسے ایک مکمل حج اور عمرے کا ثواب ملتا ہے۔ (ترمذی)',
        exception: null
      },
      zawal: {
        label: 'وقتِ زوال (سورج کا عین میزان)',
        note: 'حرام — ظہر سے چند منٹ پہلے جب سورج بالکل عین میزان پر ہو (٥–١٥ منٹ) تو نماز ممنوع ہے۔ اس لمحے پڑھی گئی نماز حنفی مذہب کے مطابق باطل ہوتی ہے۔',
        exception: 'سجدۂ تلاوت جائز ہے البتہ مکروہ تنزیہی ہے۔'
      },
      afterAsr: {
        label: 'عصر فرض کے بعد',
        note: 'مکروہ تحریمی — عصر کا فرض ادا کرنے کے بعد سورج کی زردی شروع ہونے تک نفل نماز ناجائز ہے۔ عصر کی چار مؤکد سنتیں فرض سے پہلے پڑھی جائیں، بعد میں نہیں۔',
        exception: 'فائتہ فرض نمازوں کی قضا اس وقت میں جائز ہے۔'
      },
      sunset: {
        label: 'وقتِ غروبِ آفتاب',
        note: 'حرام — جب سورج کی تپش کم ہو جائے اور سیدھا دیکھا جا سکے (مغرب سے تقریباً ٢٠ منٹ پہلے) اس وقت سے مکمل غروب تک نماز حرام ہے اور باطل ہوتی ہے۔',
        exception: 'استثناء: اگر اس دن عصر کا فرض ابھی تک نہیں پڑھا تو اسے اس وقت میں ادا کیا جا سکتا ہے اور وہ معتبر ہوگی۔'
      }
    },
    shafii: {
      afterFajr: {
        label: 'فجر نماز کے بعد',
        note: 'نفلِ مطلق کے لیے مکروہ تحریمی (بلا پیشگی سبب)۔ شافعی مذہب میں ممنوع اوقات کی نمازیں باطل نہیں ہوتیں — معتبر رہتی ہیں۔ یہ پابندی فرض ادا کرنے کے بعد سے شروع ہوتی ہے، اذان سے نہیں۔',
        exception: 'ذو السبب استثناء: جن نمازوں کا پیشگی سبب ہو — تحیۃ المسجد، وضو کے بعد کی نماز، نمازِ کسوف — یہ جائز ہیں۔ فائتہ نمازوں کی قضا بھی جائز ہے۔'
      },
      sunrise: {
        label: 'وقتِ طلوعِ آفتاب',
        note: 'نفلِ مطلق کے لیے مکروہ تحریمی، طلوع سے ارتفاعِ آفتاب تک (١٥–٢٠ منٹ)۔ لیکن حنفی مذہب کے برعکس شافعی مذہب میں یہ نمازیں باطل نہیں ہوتیں — گناہ ہے لیکن نماز ادا ہو جاتی ہے۔',
        exception: 'ذو السبب استثناء: سببی نفل نمازیں (تحیۃ المسجد، وضو کی نماز) جائز ہیں۔ قضا بھی جائز۔ مکہ مکرمہ میں کوئی پابندی نہیں۔'
      },
      ishraq: {
        label: 'نمازِ اشراق (جائز وقت)',
        note: 'دن کا پہلا جائز نفل وقت، طلوعِ آفتاب کے تقریباً ٢٠ منٹ بعد شروع ہوتا ہے۔ جو شخص فجر کے بعد ذکرِ الہٰی میں بیٹھا رہے یہاں تک کہ اشراق کی نماز پڑھے، اسے ایک مکمل حج اور عمرے کا ثواب ملتا ہے۔ (ترمذی)',
        exception: null
      },
      zawal: {
        label: 'وقتِ زوال (سورج کا عین میزان)',
        note: 'نفلِ مطلق کے لیے مکروہ تحریمی، ظہر سے پہلے سورج کے عین میزان پر ہونے کے وقت (٥–١٥ منٹ)۔ شافعی مذہب میں یہ نماز گو ناپسندیدہ ہے لیکن صحیح ہے۔',
        exception: 'جمعے کا استثناء: جمعے کے دن زوال کے وقت نفل جائز ہے۔ ذو السبب نمازیں جائز ہیں۔ مکہ مکرمہ میں کوئی پابندی نہیں۔'
      },
      afterAsr: {
        label: 'عصر نماز کے بعد',
        note: 'نفلِ مطلق کے لیے مکروہ تحریمی، عصر کے فرض کے بعد سورج کی زردی شروع ہونے تک۔ اس وقت کی تمام نمازیں شافعی مذہب میں معتبر ہیں۔',
        exception: 'ذو السبب نمازیں جائز ہیں۔ قضا بھی جائز۔ مکہ میں کوئی پابندی نہیں۔'
      },
      sunset: {
        label: 'وقتِ غروبِ آفتاب',
        note: 'نفلِ مطلق کے لیے مکروہ تحریمی، سورج کی زردی سے مکمل غروب تک (تقریباً ٢٠ منٹ)۔ شافعی مذہب میں یہ نمازیں باطل نہیں ہوتیں۔',
        exception: 'ذو السبب نمازیں جائز ہیں۔ قضا بھی جائز۔ مکہ میں کوئی پابندی نہیں۔'
      }
    },
    hanbali: {
      afterFajrAdhan: {
        label: 'اذانِ فجر کے بعد',
        note: 'حنبلی مذہب میں پابندی اذانِ فجر سے شروع ہوتی ہے — جو دیگر تمام مذاہب سے پہلے ہے۔ اذانِ فجر سے لے کر طلوعِ آفتاب تک کوئی بھی نفل نماز جائز نہیں۔',
        exception: 'فجر کی مؤکد سنت فرض سے پہلے پڑھی جا سکتی ہے۔ قضا، منت کی نماز اور نمازِ طواف جائز ہیں۔'
      },
      sunrise: {
        label: 'وقتِ طلوعِ آفتاب',
        note: 'طلوعِ آفتاب سے ارتفاعِ آفتاب (١٥–٢٠ منٹ) تک نفل ممنوع ہے — یہ اذانِ فجر کے بعد کے وقت کا تسلسل ہے اور اسی کا حکم رکھتا ہے۔',
        exception: 'قضا، منت کی نماز، نمازِ جنازہ (میت خراب ہونے کا خدشہ ہو) اور نمازِ طواف جائز ہیں۔'
      },
      ishraq: {
        label: 'نمازِ اشراق (جائز وقت)',
        note: 'دن کا پہلا جائز نفل وقت، طلوعِ آفتاب کے تقریباً ٢٠ منٹ بعد شروع ہوتا ہے۔ جو شخص فجر کے بعد ذکرِ الہٰی میں بیٹھا رہے یہاں تک کہ اشراق کی نماز پڑھے، اسے ایک مکمل حج اور عمرے کا ثواب ملتا ہے۔ (ترمذی)',
        exception: null
      },
      zawal: {
        label: 'وقتِ زوال (سورج کا عین میزان)',
        note: 'ظہر سے پہلے سورج کے عین میزان پر ہونے کے وقت (٥–١٥ منٹ) نفل ممنوع ہے۔ حنبلی مذہب میں بھی یہی حکم لاگو ہوتا ہے۔',
        exception: 'قضا، منت کی نماز، نمازِ طواف، اور جماعت میں اعادۂ نماز جائز ہے۔'
      },
      afterAsr: {
        label: 'عصر فرض کے بعد',
        note: 'عصر کا فرض ادا کرنے کے بعد مکمل غروبِ آفتاب تک نفل ممنوع ہے۔ اہم بات: حنبلی مذہب میں یہ وقت مکمل غروب تک ہے — اصفرار اور غروب کی الگ الگ تقسیم نہیں کی جاتی۔',
        exception: 'قضا، منت کی نماز، نمازِ جنازہ (میت خراب ہونے کا خدشہ)، نمازِ طواف، اور جماعت میں اعادۂ نماز جائز ہے۔'
      }
    },
    maliki: {
      sunrise: {
        label: 'وقتِ طلوعِ آفتاب',
        note: 'مکروہ — سورج کے طلوع کے دوران نفل نماز مکروہ ہے، افق پر نمودار ہونے سے مکمل ارتفاع تک (١٥–٢٠ منٹ)۔ نماز معتبر رہتی ہے لیکن ناپسندیدہ ہے۔',
        exception: 'فائتہ نمازوں کی قضا جائز ہے۔ فرائض متاثر نہیں ہوتے۔'
      },
      ishraq: {
        label: 'نمازِ اشراق (جائز وقت)',
        note: 'دن کا پہلا جائز نفل وقت، طلوعِ آفتاب کے تقریباً ٢٠ منٹ بعد شروع ہوتا ہے۔ جو شخص فجر کے بعد ذکرِ الہٰی میں بیٹھا رہے یہاں تک کہ اشراق کی نماز پڑھے، اسے ایک مکمل حج اور عمرے کا ثواب ملتا ہے۔ (ترمذی)',
        exception: null
      },
      yellowSun: {
        label: 'اصفرارِ شمس (مغرب سے پہلے)',
        note: 'مکروہ — مغرب سے پہلے جب سورج کی روشنی زرد پڑ جائے (تقریباً ٢٠ منٹ) تو نفل نماز مکروہ ہے۔ مالکی مذہب میں صرف دو ناپسندیدہ اوقات ہیں اور یہ ان میں سے ایک ہے۔ نماز معتبر رہتی ہے۔',
        exception: 'فائتہ نمازوں کی قضا جائز ہے۔ فرائض متاثر نہیں ہوتے۔'
      },
      malikirule: {
        label: 'نوٹ: زوال کا وقت ممنوع نہیں',
        note: 'تینوں دیگر مذاہب کے برعکس مالکی مذہب میں زوال کے وقت نماز مکروہ نہیں ہے۔ ظہر سے پہلے زوال کے وقت نماز پڑھنا مالکی مذہب میں بالکل جائز ہے — کوئی کراہت نہیں۔',
        exception: null
      },
      malikirule2: {
        label: 'خاص مالکی حکم: پہلے قضا',
        note: 'مالکی مذہب کا ایک امتیازی حکم: جس شخص کے ذمے فائتہ فرض نمازیں ہوں، اسے نفل پڑھنے سے پہلے ان کی قضا ادا کرنی چاہیے۔ یہ ایک عمومی اصول ہے جو صرف ممنوع اوقات تک محدود نہیں۔',
        exception: null
      }
    }
  }
};

/* ── Geometry + derived-time compute (from AP_PRAYER_HERO.applyTimings) ─────── */

const ARC_EDGE_PAD = 5;

export const toMin = (t: string): number => {
  const p = t.split(':');
  return Number(p[0]) * 60 + Number(p[1]);
};
const pad = (n: number): string => String(n).padStart(2, '0');
const minToHH = (m: number): string => pad(Math.floor(m / 60)) + ':' + pad(m % 60);
const addMin = (hhmm: string, add: number): string => {
  const m = (toMin(hhmm) + add + 1440) % 1440;
  return minToHH(m);
};

export interface ArcPrayer {
  key: string;
  name: string;
  ar: string;
  time: string;
}

export interface DerivedTimes {
  prayers: ArcPrayer[];
  sunrise: string;
  sunset: string;
  ishraq: string;
  zawalStart: number;
  zawalEnd: number;
  fajrFardDone: string;
  asrFardDone: string;
  sunsetBegins: string;
  /** Arc forbidden zones, with resolved fromMin/toMin in minutes. */
  zones: Array<{ key: string; strict: boolean; fromMin: number; toMin: number; label: string; ar: string }>;
}

const ARC_PRAYER_META: Array<{ key: keyof import('./types').PrayerTimings; name: string; ar: string }> = [
  { key: 'fajr', name: 'Fajr', ar: 'الفجر' },
  { key: 'dhuhr', name: 'Dhuhr', ar: 'الظهر' },
  { key: 'asr', name: 'Asr', ar: 'العصر' },
  { key: 'maghrib', name: 'Maghrib', ar: 'المغرب' },
  { key: 'isha', name: 'Isha', ar: 'العشاء' }
];

export interface FardBuffers {
  fajr: number;
  asr: number;
}

/** Reproduces the widget's derived-window math. `timings` are "HH:MM". */
export function computeDerivedTimes(
  timings: import('./types').PrayerTimings,
  fardBuffers: FardBuffers = { fajr: 15, asr: 15 }
): DerivedTimes {
  const prayers: ArcPrayer[] = ARC_PRAYER_META.map((m) => ({
    key: m.key,
    name: m.name,
    ar: m.ar,
    time: String(timings[m.key]).slice(0, 5)
  }));
  const sunrise = String(timings.sunrise).slice(0, 5);
  const sunset = String(timings.maghrib).slice(0, 5); // PH_SUNSET tracks Maghrib in the widget
  const dhuhrMin = toMin(timings.dhuhr.slice(0, 5));
  const zawalStart = dhuhrMin - 12;
  const zawalEnd = dhuhrMin;
  const fajrFardDone = addMin(timings.fajr.slice(0, 5), fardBuffers.fajr);
  const asrFardDone = addMin(timings.asr.slice(0, 5), fardBuffers.asr);
  const sunsetBegins = addMin(sunset, -20);
  const ishraq = addMin(sunrise, 20);

  const zones: DerivedTimes['zones'] = [
    { key: 'afterFajr', strict: false, fromMin: toMin(fajrFardDone), toMin: toMin(sunrise), label: 'After Fajr fard', ar: 'بعد فرض الفجر' },
    { key: 'sunrise', strict: true, fromMin: toMin(sunrise), toMin: toMin(ishraq), label: 'Sunrise', ar: 'الشروق' },
    { key: 'zawal', strict: true, fromMin: zawalStart, toMin: zawalEnd, label: 'Zawal', ar: 'الزوال' },
    { key: 'afterAsr', strict: false, fromMin: toMin(asrFardDone), toMin: toMin(sunsetBegins), label: 'After Asr fard', ar: 'بعد فرض العصر' },
    { key: 'sunset', strict: true, fromMin: toMin(sunsetBegins), toMin: toMin(sunset), label: 'Sunset', ar: 'الغروب' }
  ];

  return { prayers, sunrise, sunset, ishraq, zawalStart, zawalEnd, fajrFardDone, asrFardDone, sunsetBegins, zones };
}

/** Arc X% for a given minute, spanning first→last prayer with edge padding. RTL mirrors. */
export function arcPos(mins: number, prayers: ArcPrayer[], isRTL: boolean): number {
  const start = toMin(prayers[0].time);
  const end = toMin(prayers[prayers.length - 1].time);
  let x: number;
  if (mins <= start) x = ARC_EDGE_PAD;
  else if (mins >= end) x = 100 - ARC_EDGE_PAD;
  else x = ARC_EDGE_PAD + ((mins - start) / (end - start)) * (100 - ARC_EDGE_PAD * 2);
  return isRTL ? 100 - x : x;
}

export interface ActiveRestricted {
  key: string;
  strict: boolean;
  toMin: number;
}

/** Find the restricted window the given minute falls in, for the warning banner (madhab-aware via zones). */
export function findActiveRestrictedWindow(nowMin: number, zones: DerivedTimes['zones']): ActiveRestricted | null {
  for (const z of zones) {
    if (!z.toMin) continue;
    if (nowMin >= z.fromMin && nowMin < z.toMin) {
      return { key: z.key, strict: z.strict, toMin: z.toMin };
    }
  }
  return null;
}

export function formatClock(hhmm: string, fmt: '12h' | '24h'): string {
  if (fmt !== '12h') return hhmm;
  const p = hhmm.split(':');
  let h = Number(p[0]);
  const m = Number(p[1]);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return h + ':' + pad(m) + ' ' + suffix;
}

export function formatRange(fromHH: string, toHH: string, fmt: '12h' | '24h'): string {
  if (fmt === '12h') {
    const fromParts = formatClock(fromHH, fmt).split(' ');
    const toParts = formatClock(toHH, fmt).split(' ');
    if (fromParts[1] && fromParts[1] === toParts[1]) {
      return fromParts[0] + '-' + toParts[0] + ' ' + toParts[1];
    }
  }
  return formatClock(fromHH, fmt) + '-' + formatClock(toHH, fmt);
}

export { minToHH };
